<template>
  <div class="ndfe-native-select-wrapper">
    <v-select
      v-bind="valueBinding"
      v-on="$listeners"
      key="core-select"
      ref="coreSelect"
      :style="isMobile ? 'pointer-events: none' : ''"
      @click="clickToCloseOnFocus"
    >
      <slot v-for="(_, name) in $slots" :name="name" :slot="name" />
      <template v-for="(_, name) in $scopedSlots" :slot="name" slot-scope="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
    </v-select>

    <select
      v-bind="valueBinding"
      v-show="isMobile"
      class="ndfe-native-select"
      ref="nativeSelectMobile"
      :value="JSON.stringify($props.value)"
      @change="syncValue"
      @blur="syncValueForIphone"
    >
      <template v-if="isGroup">
        <optgroup
          v-for="(make, idx) in $props.groupItems"
          :key="`${make.label}-${idx}`"
          :label="make.label"
        >
          <option
            v-for="(model, idx) in make.models"
            :key="`${model.label}-${idx}`"
            :value="getValue(model)"
            :selected="compare(model)"
            :disabled="typeof model === 'object' ? model.disabled : false"
          >
            <template v-if="model.value === -1">
              {{ typeof model === 'object' ? model.text : model }}
            </template>
            <template v-else>
              {{ typeof model === 'object' ? model.text : model }}
              <template v-if="model.count"> ({{ model.count }})</template>
            </template>
          </option>
        </optgroup>
      </template>

      <template v-else>
        <option v-if="showPlaceholder" :id="`disabled-option-${label}`" value="" selected>
          - {{ $t('CORE.SELECT') }} {{ label }} -
        </option>
        <option
          v-for="(item, i) in $props.items"
          :value="getValue(item)"
          :key="`${item.text}_${i}`"
          :selected="compare(item)"
          :disabled="typeof item === 'object' ? item.disabled : false"
        >
          <template v-if="item.value === -1">
            {{ typeof item === 'object' ? item.text : item }}
          </template>
          <template v-else>
            {{ typeof item === 'object' ? item.text : item }}
            <template v-if="item.count"> ({{ item.count }})</template>
          </template>
        </option>
      </template>
    </select>
  </div>
</template>

<script>
import { VSelect } from 'vuetify/lib';

import(/* webpackChunkName: "VSelect" */ '@/styles/vuetify/VSelect.scss');

export default {
  isCustomElement: true,
  name: 'core-select',
  components: {
    VSelect,
  },
  props: {
    value: null,
    groupItems: [],
    items: [],
    isGroup: {
      type: Boolean,
      default: false,
    },
    filterName: {
      type: String,
      default: '',
    },
    showPlaceholder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      count: 0,
    };
  },
  methods: {
    clickToCloseOnFocus() {
      const refs = this.$refs.coreSelect;
      if (!refs) return;
      const vMenuChild = refs.$children.find(
        (childComponent) => childComponent.$options.name === 'v-menu',
      );
      if (!vMenuChild || !vMenuChild.$data.hasJustFocused) return;
      refs.blur();
    },

    // Get value in an optin
    getValue(item) {
      if (typeof item !== 'object') return item;
      if (typeof item.value === 'object') return JSON.stringify(item.value);
      return item.value || item.text;
    },
    isJson(str) {
      try {
        JSON.parse(str);
      } catch (e) {
        return false;
      }
      return true;
    },
    syncValue() {
      // not update on iphone
      if (this.isIphone) return;

      this.updateValue();
    },
    /**
     * syncValueForIphone - update value for iphone behavior
     * the issue has been mentioned at: https://discussions.apple.com/thread/5548742?page=1
     */
    syncValueForIphone() {
      if (!this.isIphone) return;

      this.updateValue();
    },
    updateValue() {
      let value;

      if (this.valueBinding.multiple === '' || this.valueBinding.multiple) {
        value = Array.from(this.$refs.nativeSelectMobile.selectedOptions).map((option) => {
          if (this.isJson(option.value)) return JSON.parse(option.value);
          return option.value;
        });
      } else {
        value = this.isJson(this.$refs.nativeSelectMobile.value)
          ? JSON.parse(this.$refs.nativeSelectMobile.value)
          : this.$refs.nativeSelectMobile.value;
      }
      this.count += 1;
      this.$emit('input', value);
      this.$emit('change', value);
    },
    compare(item) {
      return Array.isArray(this.$props.value)
        ? this.$props.value.includes(this.getValue(item))
        : this.getValue(item) === JSON.stringify(this.$props.value);
    },
  },
  computed: {
    valueBinding() {
      return { ...this.$attrs, ...this.$props };
    },
    label() {
      return (this.valueBinding.label || '').toLowerCase();
    },
    isIphone() {
      return /iPhone|SamsungBrowser/i.test(navigator.userAgent);
    },
  },
  watch: {
    count(newVal) {
      if (!this.showPlaceholder || (newVal < 1) || !this.valueBinding.required) return;
      const ele = document.getElementById(`disabled-option-${this.label}`);
      if (!ele || this.valueBinding.items?.length <= 1) return;
      ele.setAttribute('disabled', true);
    },
    items: {
      deep: true,
      handler(newVal, oldVal) {
        if (!this.showPlaceholder || (JSON.stringify(newVal) === JSON.stringify(oldVal))
          || !this.valueBinding.required) return;
        const ele = document.getElementById(`disabled-option-${this.label}`);
        if (ele) ele.removeAttribute('disabled');
      },
    },
  },
};
</script>

<style lang="scss">
.ndfe-native-select-wrapper {
  position: relative;
  .v-select__selections {
    input {
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
    }
  }
  .ndfe-native-select {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    outline: 0;
    overflow: hidden;
    padding: 0;
    opacity: 0;
    font-size: 16px;
  }
}
</style>
